import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { PartnerTemplatePartner } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import { Button, toastCaller } from '@/shared/ui';

import { getPartnerTemplateName } from '@/entities/templates';

import { i18n } from '../config';
import { usePartnerExport } from '../hooks';

import { PartnerExportWarnings } from './PartnerExportWarnings';
import styles from './PartnerNoteCopy.module.scss';

type Props = {
  partner: PartnerTemplatePartner;
  encounterId: string;
  documentId: string;
  revision: number;
  // Open lines, copied with the template's defaults.
  defaultedLineCount: number;
  listening: boolean;
  editsPending: boolean;
  // Why the copy waits for the clinician, worded as for the regular copy.
  blockedHint?: string;
  describeLine?: (lineId: string) => string;
  onShowLine?: (lineId: string) => void;
};

type CopyFailure = 'content' | 'plainText' | null;

// The write starts within the click, before any await: Safari refuses a
// clipboard write once the click's gesture is gone. A missing clipboard
// (outside secure contexts) throws here and rejects the promise.
const writeClipboard = (text: string) =>
  new Promise<void>((resolve) => resolve(navigator.clipboard.writeText(text)));

// The note copied as its partner template's text, laid out as items of the
// note's toolbar. Unfilled lines keep the partner template's defaults and
// skipped ones are switched off, so the copy does not wait for every line;
// the places the text cannot carry exactly are listed beside it.
export const PartnerNoteCopy: FC<Props> = ({
  partner,
  encounterId,
  documentId,
  revision,
  defaultedLineCount,
  listening,
  editsPending,
  blockedHint,
  describeLine,
  onShowLine,
}) => {
  const { formatMessage } = useIntl();
  const [failure, setFailure] = useState<CopyFailure>(null);
  const partnerExport = usePartnerExport({
    encounterId,
    documentId,
    revision,
    listening,
    editsPending,
  });
  const partnerName = getPartnerTemplateName(formatMessage, partner);
  const result = partnerExport.status === 'ready' ? partnerExport.result : null;

  const getWaitingHint = () => {
    switch (partnerExport.status) {
      case 'listening':
        return formatMessage(
          {
            id: 'workspace.note.partnerAfterRecording',
            defaultMessage:
              'The text for {partner} is prepared once the recording stops',
          },
          { partner: partnerName },
        );
      case 'loading':
        return formatMessage(
          {
            id: 'workspace.note.partnerPreparing',
            defaultMessage: 'Preparing the text for {partner}…',
          },
          { partner: partnerName },
        );
      default:
        return undefined;
    }
  };

  const copyContent = () => {
    if (!result) {
      return;
    }
    const warningCount = result.warnings.length;
    writeClipboard(result.content)
      .then(() => {
        setFailure(null);
        toastCaller({
          type: 'success',
          message: warningCount
            ? formatMessage(
                {
                  id: 'workspace.note.partnerCopiedWarnings',
                  defaultMessage:
                    'Copied for {partner}. {count, plural, one {# place of the note} other {# places of the note}} could not be carried over exactly.',
                },
                { partner: partnerName, count: warningCount },
              )
            : formatMessage(
                {
                  id: 'workspace.note.partnerCopied',
                  defaultMessage: 'Copied for {partner}',
                },
                { partner: partnerName },
              ),
        });
      })
      .catch(() => setFailure('content'));
  };

  const copyPlainText = () => {
    if (!result) {
      return;
    }
    writeClipboard(result.plainText)
      .then(() => {
        setFailure(null);
        toastCaller({
          type: 'success',
          message: formatMessage({
            id: 'workspace.note.copied',
            defaultMessage: 'Copied',
          }),
        });
      })
      .catch(() => setFailure('plainText'));
  };

  return (
    <>
      <Button
        variant="secondary"
        size="small"
        icon="copy"
        disabled={!result || !!blockedHint}
        title={blockedHint ?? getWaitingHint()}
        data-component="workspace-note-partner-copy"
        onClick={copyContent}
      >
        <FormattedMessage
          id="workspace.note.copyForPartner"
          defaultMessage="Copy for {partner}"
          values={{ partner: partnerName }}
        />
      </Button>
      {partnerExport.status === 'failed' && (
        <span role="status" className={cn('p4', styles.failed)}>
          <FormattedMessage
            id="workspace.note.partnerExportFailed"
            defaultMessage="Could not prepare the text for {partner}."
            values={{ partner: partnerName }}
          />
          {partnerExport.refusal &&
            ` ${formatMessage(
              i18n.partnerExportRefusalMessages[partnerExport.refusal],
              { partner: partnerName },
            )}`}{' '}
          <button
            type="button"
            className={cn('p4', styles.link)}
            onClick={partnerExport.retry}
          >
            <FormattedMessage id="global.retry" defaultMessage="Try again" />
          </button>
        </span>
      )}
      {failure === 'content' && result && (
        <span role="status" className={cn('p4', styles.failed)}>
          <FormattedMessage
            id="workspace.note.partnerCopyFailed"
            defaultMessage="Could not copy the text for {partner}."
            values={{ partner: partnerName }}
          />{' '}
          <button
            type="button"
            className={cn('p4', styles.link)}
            data-component="workspace-note-partner-copy-plain"
            onClick={copyPlainText}
          >
            <FormattedMessage
              id="workspace.note.copyPlainText"
              defaultMessage="Copy as plain text"
            />
          </button>
        </span>
      )}
      {failure === 'plainText' && (
        <span role="status" className={cn('p4', styles.failed)}>
          <FormattedMessage
            id="workspace.note.copyFailed"
            defaultMessage="Could not copy. Select the text in the document instead."
          />
        </span>
      )}
      {defaultedLineCount > 0 && (
        <span
          className={cn('p4', styles.hint)}
          data-component="workspace-note-partner-hint"
        >
          <FormattedMessage
            id="workspace.note.partnerDefaults"
            defaultMessage="{count, plural, one {# line not filled} other {# lines not filled}} — the template's defaults are copied"
            values={{ count: defaultedLineCount }}
          />
        </span>
      )}
      {result && (
        <PartnerExportWarnings
          warnings={result.warnings}
          partnerName={partnerName}
          describeLine={describeLine}
          onShowLine={onShowLine}
        />
      )}
    </>
  );
};
